"use client";

// Live "capital deployed" ticker (Design QA ref: slash.com hero counter).
// NumberFlow renders odometer-style rolling digits and handles SSR (the
// initial value is server-rendered as static text) and reduced motion.
// On mount it briefly "scrambles" up to the real starting figure (a slot
// -machine-style reveal), then ticks up by realistic loan-sized amounts —
// never by a few hundred dollars, since that's not a size Wayflyer funds.

import NumberFlow from "@number-flow/react";
import * as React from "react";

import { convertUsdAmount } from "@/lib/converted-amount";
import { LOCALE_CURRENCY } from "@/lib/locale-prefix";

// Representative loan-drawdown sizes (USD). Smaller amounts repeat so they
// come up more often than the rarer large ones — no loan is ever a few
// hundred dollars, so nothing below 10k appears here.
const LOAN_INCREMENTS = [
  10_000, 10_000, 15_000, 15_000, 20_000, 25_000, 30_000, 40_000, 40_000,
  50_000, 75_000, 100_000, 150_000, 200_000, 250_000, 500_000,
];

function randomLoanIncrement() {
  const base = LOAN_INCREMENTS[
    Math.floor(Math.random() * LOAN_INCREMENTS.length)
  ] as number;
  // Random hundreds/tens/ones on top so totals don't always land on a
  // clean round thousand — reads as a real, ever-changing figure.
  const smallChange = Math.floor(Math.random() * 1000);
  return base + smallChange;
}

interface DeployedCounterProps {
  /** Starting figure in USD. */
  value: number;
  /** BCP-47 locale for digit grouping. */
  locale: string;
  /** Milliseconds between "a loan just deployed" ticks. */
  intervalMs?: number;
}

export function DeployedCounter({
  value,
  locale,
  intervalMs = 5000,
}: DeployedCounterProps) {
  const currency = LOCALE_CURRENCY[locale] ?? "USD";
  const localeValue = React.useMemo(
    () => convertUsdAmount(value, locale),
    [locale, value]
  );
  // Server/initial paint renders the real value as static text — no flash
  // of a placeholder number before hydration.
  const [current, setCurrent] = React.useState(localeValue);
  const [hasSettled, setHasSettled] = React.useState(false);

  // Scramble-in intro: once mounted, rapidly roll through nearby numbers
  // that converge on a starting figure, landing on it at the last step.
  // That figure is `value` plus a small random offset — computed client
  // -side (not during the deterministic SSR render) so the number never
  // starts on a suspiciously flat round figure.
  React.useEffect(() => {
    const target = localeValue + Math.floor(Math.random() * 1000);
    const STEPS = 12;
    const DURATION_MS = 700;
    let step = 0;

    const id = setInterval(() => {
      step += 1;
      if (step >= STEPS) {
        setCurrent(target);
        setHasSettled(true);
        clearInterval(id);
        return;
      }
      const progress = step / STEPS;
      // Noise band shrinks to zero as the reveal approaches the target.
      const noise = target * 0.08 * (1 - progress) * Math.random();
      setCurrent(Math.round(target * progress + noise));
    }, DURATION_MS / STEPS);

    return () => clearInterval(id);
  }, [localeValue]);

  // Once settled, tick up by one realistic loan amount per interval.
  React.useEffect(() => {
    if (!hasSettled) return;
    const id = setInterval(() => {
      setCurrent((prev) => prev + randomLoanIncrement());
    }, intervalMs);
    return () => clearInterval(id);
  }, [hasSettled, intervalMs]);

  return (
    <NumberFlow
      value={current}
      locales={locale}
      format={{ style: "currency", currency, maximumFractionDigits: 0 }}
      className="tabular-nums"
    />
  );
}
